import { useEffect, useState } from 'react';
import { Button, Modal, SkeletonBodyText, TextField } from '@shopify/polaris';
import Language from '../../i18n/Language';
import { reqGetAccount, reqUpdExcessLimit } from '../../api';
import './index.scss';

const t = Language;

// 语言 -> 日期本地化
const LANG_LIST = {
    cs: 'cs-CZ', en: 'en-GB', da: 'da-DK', es: 'es-ES', fr: 'fr-FR', de: 'de-DE',
    in: 'hi-IN', it: 'it-IT', nl: 'nl-NL', nb: 'no-NO', pl: 'pl-PL', pt: 'pt-PT',
    pt_BR: 'pt-BR', fi: 'fi-FI', sv: 'sv-SE', tr: 'tr-TR', th: 'th-TH', ja: 'ja-JP',
    ko: 'ko-KR', zh: 'zh-CN', zh_hant: 'zh-TW',
};

// 额度重置日期：18 March 2026
const formatResetDate = (timestamp, lang = 'en') => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString(LANG_LIST[lang] || 'en-GB', options);
};

/**
 * 超额已达上限提示弹窗
 * 出现时机：使用超额达到了超额额度上限
 * @param {boolean} open            是否打开
 * @param {function} onClose        关闭回调
 * @param {string} language         当前语言（用于日期本地化）
 * @param {function} onWaitForReset 点击「Wait for reset」
 * @param {function} onContact      点击「Contact us」
 */
const OverageFeeModal = (props) => {
    const {
        open,
        onClose,
        language = 'en',
        onWaitForReset,
        onContact,
    } = props;

    const [loading, setLoading] = useState(true);
    // 超额已使用金额（美元）
    const [overchargeUse, setOverchargeUse] = useState(0);
    // 超额的图片额度（使用量 / 总超额额度）
    const [exceedUse, setExceedUse] = useState(0);
    const [exceedAll, setExceedAll] = useState(0);
    // 超额的积分额度（使用量 / 总超额额度）
    const [pointsPaidUse, setPointsPaidUse] = useState(0);
    const [pointsPaidAll, setPointsPaidAll] = useState(0);
    // 额度重置日期
    const [resetDate, setResetDate] = useState('');
    // 当前账单设置的超额上限 / 输入值
    const [selectCap, setSelectCap] = useState(0);
    const [originCap, setOriginCap] = useState(0);
    // 超额错误提示 & 是否达到 Shopify 上限
    const [overchargeError, setOverchargeError] = useState('');
    const [isLimitMax, setIsLimitMax] = useState(false);
    // Raise limit 请求中
    const [submitting, setSubmitting] = useState(false);

    // 是否向上调整了上限（可提交）
    const isShowUpdate = Number(selectCap) > Number(originCap);
    // Raise limit 按钮可用状态：需向上调整且无错误
    const canRaise = isShowUpdate && !overchargeError;

    // 获取账户额度数据
    const getAccount = async () => {
        setLoading(true);
        const res = await reqGetAccount();
        if (res && res.meal_data) {
            const m = res.meal_data;
            // 超额的图片额度
            setExceedUse(Number(m.img_usage_use || 0));
            setExceedAll(Number(m.img_usage_all || 0));
            // 超额的积分额度
            setPointsPaidUse(Number(m.points_paid_use || 0));
            setPointsPaidAll(Number(m.points_paid_all || 0));
            setResetDate(formatResetDate(res.reset_quota * 1000, language));
            // 已使用金额 / 原来金额上限
            setOverchargeUse(Number(res.balance_used || 0));
            const cap = Number(res.capped_amount || 0);
            setOriginCap(cap);
            // 输入的额度上限默认值为：账单上限 + 100
            setSelectCap(cap + 100);
            setIsLimitMax(Boolean(res.is_excess_limit));
        }
        setLoading(false);
    };

    useEffect(() => {
        if (open) {
            getAccount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 输入超额（参考 Settings/indexClass handleOverchargeChange）
    const handleOverchargeChange = (e) => {
        setSelectCap(e);
        setOverchargeError('');
    };

    // 步进器（参考 Settings/indexClass onSpinnerOvercharge）
    const onSpinnerOvercharge = (e) => {
        const _n = Number(selectCap);
        const newCap = _n < Number(e) ? _n + 100 : _n < 100 ? _n - 1 : _n - 100;
        if (newCap >= 0) {
            setSelectCap(newCap);
            setOverchargeError('');
        }
    };

    // 校验并更新超额上限（参考 Settings/indexClass changeCapValue）
    const handleRaiseLimit = async () => {
        if (Number(selectCap) < Number(originCap)) {
            setOverchargeError(t('Please enter an amount above ${{v1}} USD', originCap));
            return;
        }
        if (!canRaise) return;
        setOverchargeError('');
        setSubmitting(true);
        const res = await reqUpdExcessLimit({ amount: Number(selectCap) });
        setSubmitting(false);
        if (res && res.ack === 1) {
            setOriginCap(Number(selectCap));
            if (res.data && res.data.confirmationUrl) {
                setTimeout(() => {
                    window.open(res.data.confirmationUrl);
                }, 500);
            }
        } else {
            setOverchargeError((res && res.msg) || '');
        }
    };

    // 超额额度用量卡片（仅展示有超额使用的项）
    const showImage = exceedAll > 0;
    const showPoints = pointsPaidAll > 0;
    const showQuota = showImage || showPoints;

    return (
        <Modal open={Boolean(open)} title={t("You've reached your spending limit for this billing cycle")} onClose={onClose}>
            <div className="overage_fee_modal_content">
                {/* Content */}
                <div className="ofm_content">
                    {loading ? (
                        <SkeletonBodyText lines={8} />
                    ) : (
                        <>
                            <p className="ofm_desc">
                                {t('Your monthly quota resets on')}{' '}
                                <b className="ofm_desc_date">{resetDate}</b>.&nbsp;
                                {t('You can wait for the reset at no additional cost, or raise your spending limit to continue using image optimization or AI features.')}
                            </p>

                            {/* Spending this cycle */}
                            <div className="ofm_spending_card">
                                <div className="ofm_spending_header">
                                    <span className="ofm_spending_label">{t('Spending this cycle')}</span>
                                    <span className="ofm_spending_reset">{t('Resets {{v1}}', resetDate)}</span>
                                </div>
                                <div className="ofm_spending_progress">
                                    <div className="ofm_progress">
                                        <div
                                            className="ofm_progress_bar"
                                            style={{ width: `${originCap > 0 ? Math.min(100, (overchargeUse / originCap) * 100) : 0}%` }}
                                        />
                                    </div>
                                    <div className="ofm_spending_usage">
                                        <span className="ofm_spending_used">${overchargeUse}</span>
                                        <span className="ofm_spending_total">{t('of ${{v1}} limit reached', originCap)}</span>
                                    </div>
                                </div>

                                {/* 超额额度（只展示超额的项） */}
                                {showQuota && (
                                    <div className="ofm_quota">
                                        {showImage && (
                                            <p className="ofm_quota_item">
                                                {t('Add-on Image Quota: {{v1}}/{{v2}} used', exceedUse, exceedAll)}
                                            </p>
                                        )}
                                        {showPoints && (
                                            <p className="ofm_quota_item">
                                                {t('Add-on AI Credits: {{v1}}/{{v2}} used', pointsPaidUse, pointsPaidAll)}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* New Spending limit */}
                            <div className="ofm_spending">
                                <p className="ofm_spending_title">{t('New Spending limit')}</p>
                                <p className="ofm_spending_desc">
                                    {t('Set your spending limit for additional usage.')}{' '}
                                    <span className="ofm_link" onClick={onContact}>
                                        {t('Questions about charges? Contact us.')}
                                    </span>
                                </p>
                                <div className="ofm_spending_input">
                                    <div className="overcharg_setting">
                                        <TextField
                                            type="number"
                                            value={selectCap}
                                            onChange={handleOverchargeChange}
                                            onSpinnerChange={onSpinnerOvercharge}
                                            prefix="$"
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                {overchargeError || Number(selectCap) < Number(originCap) ? (
                                    <p className="ofm_spending_error">
                                        {overchargeError || t("Due to Shopify's billing settings, please enter a spending limit of {{v1}} or more.", originCap)}
                                    </p>
                                ) : isLimitMax && !isShowUpdate ? (
                                    <p className="ofm_spending_error">
                                        {t('The App spending limit has been reached, no further charges can be made.')}
                                    </p>
                                ) : isShowUpdate ? (
                                    <p className="ofm_spending_hint">
                                        {t("You'll be redirected to Shopify to approve the new limit")}
                                    </p>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="ofm_footer">
                    <Button disabled={loading || submitting} onClick={onWaitForReset}>
                        {t('Wait for reset')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleRaiseLimit}
                        loading={submitting}
                        disabled={loading || !canRaise}
                    >
                        {t('Raise limit')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default OverageFeeModal;
